import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Checkbox, FormControlLabel, IconButton, Typography, useTheme } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
    ArrayFieldConfig,
    CheckboxFieldConfig,
    DateFieldConfig,
    DualCheckboxFieldConfig,
    FieldConfig,
    GridConfig,
    LineLabelCheckboxFieldConfig,
    LineLabelFieldConfig,
    NumberFieldConfig,
    PageConfig,
    SectionConfig,
    TextFieldConfig,
} from "../types/formConfig";
import FormTextField from "./FormTextField";
import FormCurrencyField from "./FormCurrencyField";
import FormSelect from "./FormSelect";
import FormRadioGroup from "./FormRadioGroup";
import FormFieldArray from "./FormFieldArray";
import FormFieldStatementSections from "./FormFieldStatementSections";
import FormGrid from "./FormGrid";
import FormSection from "./FormSection";
import FormColumnarSection from "./FormColumnarSection";
import { FormMetaContext } from "./FormMetaContext";
import { RootState } from "../../../../configureStore";
import { get } from "../../../../utils";

interface FormRendererProps {
    pageConfig: PageConfig;
    formType: string;
    formName: string;
    getFieldValue: (path: string) => any;
    handleFieldChange: (path: string, value: any) => void;
    getFieldConfidence?: (path: string) => number;
    currentExtraction?: any;
}

const dualCheckboxValueToTuple = (value: unknown): [boolean, boolean] => {
    if (Array.isArray(value)) return [Boolean(value[0]), Boolean(value[1])];
    if (value === true) return [true, false];
    if (value === false) return [false, true];
    return [false, false];
};

const dualCheckboxTupleToValue = (tuple: [boolean, boolean]): boolean | undefined => {
    if (tuple[0] && !tuple[1]) return true;
    if (!tuple[0] && tuple[1]) return false;
    return undefined;
};

const hasImportantFields = (fields: FieldConfig[]): boolean => {
    const checkField = (field: FieldConfig): boolean => {
        if (field.important) return true;
        // Statement sections should always be considered as having important fields
        if (field.type === "statement-sections") return true;
        if (field.type === "array") {
            const arrayConfig = field as ArrayFieldConfig;
            // Recursively check itemFields for important fields
            return arrayConfig.itemFields?.some(checkField) ?? false;
        }
        return false;
    };
    return fields.some(checkField);
};

// Helper to check if a single field has important children (for array fields)
const fieldHasImportantChildren = (field: FieldConfig): boolean => {
    if (field.type === "array") {
        const arrayConfig = field as ArrayFieldConfig;
        return hasImportantFields(arrayConfig.itemFields || []);
    }
    return false;
};

// Wrap a rendered field with gridColumn span when colSpan > 1
const wrapWithColSpan = (node: React.ReactNode, key: string, colSpan?: number) => {
    if (colSpan && colSpan > 1) {
        return (
            <Box
                key={key}
                sx={{ gridColumn: `span ${colSpan}` }}
            >
                {node}
            </Box>
        );
    }
    return node;
};

// Build concrete fields for a single template row across N columns
const buildTemplateFields = (grid: GridConfig, columnCount: number, rowIndex?: number): FieldConfig[] => {
    // Per-column templates take precedence (each column binds to a different subpath).
    if (grid.rowFieldTemplates && grid.rowFieldTemplates.length > 0) {
        return grid.rowFieldTemplates.map((tpl) => {
            const rawFieldPath = tpl.fieldPath ?? "";
            const fieldPath =
                rowIndex !== undefined ? rawFieldPath.replace("{rowIndex}", String(rowIndex)) : rawFieldPath;
            const type = tpl.type || grid.fieldTypeTemplate || "currency";
            const label = tpl.label ?? grid.fieldLabelTemplate ?? "";
            const important = tpl.important ?? grid.fieldImportantTemplate ?? false;
            const base = { fieldPath, important, empty: tpl.empty ?? false, colSpan: tpl.colSpan };
            const stringLabel = (label as string | undefined) ?? "";
            switch (type) {
                case "currency":
                case "number":
                    return { ...base, label: stringLabel, type } satisfies NumberFieldConfig;
                case "checkbox":
                    return { ...base, label: stringLabel, type } satisfies CheckboxFieldConfig;
                case "dual-checkbox":
                    return { ...base, label: label as [string, string], type } satisfies DualCheckboxFieldConfig;
                case "date":
                    return {
                        ...base,
                        label: stringLabel,
                        type,
                        checkDateFormat: tpl.checkDateFormat,
                    } satisfies DateFieldConfig;
                default:
                    return { ...base, label: stringLabel, type: undefined } satisfies TextFieldConfig;
            }
        });
    }
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const fieldType = grid.fieldTypeTemplate || "currency";
    const lineNum = (grid.rowLabel || "").match(/^\d+/)?.[0] || "";
    return Array.from({ length: columnCount }, (_, i) => {
        let fieldPath = grid.fieldPathTemplate!.replace("{index}", String(i));
        if (rowIndex !== undefined) fieldPath = fieldPath.replace("{rowIndex}", String(rowIndex));
        const defaultLabel = `${letters[i] || i + 1}-${lineNum}`;
        const labelTemplate = grid.fieldLabelTemplate ?? defaultLabel;
        const base = {
            fieldPath,
            important: grid.fieldImportantTemplate ?? false,
        };
        const stringLabel = typeof labelTemplate === "string" ? labelTemplate : defaultLabel;
        switch (fieldType) {
            case "currency":
            case "number":
                return { ...base, label: stringLabel, type: fieldType } satisfies NumberFieldConfig;
            case "checkbox":
                return { ...base, label: stringLabel, type: fieldType } satisfies CheckboxFieldConfig;
            case "dual-checkbox":
                return {
                    ...base,
                    type: fieldType,
                    label: labelTemplate as [string, string],
                } satisfies DualCheckboxFieldConfig;
            case "date":
                return { ...base, label: stringLabel, type: fieldType } satisfies DateFieldConfig;
            case "array": {
                const { emptyItem = {}, itemFields = [], addButtonLabel, emptyMessage } = grid.fieldArrayTemplate || {};
                return {
                    ...base,
                    label: stringLabel,
                    type: "array" as const,
                    emptyItem,
                    itemFields,
                    addButtonLabel,
                    emptyMessage,
                } satisfies ArrayFieldConfig;
            }
            default:
                return { ...base, label: stringLabel, type: undefined } satisfies TextFieldConfig;
        }
    });
};

// Expand grids that use fieldPathTemplate into concrete fields based on dynamic column count
// Expandable grids (with expandableArrayField) are split into parent row + sub-rows
const expandDynamicGrids = (
    grids: GridConfig[],
    columnCount: number,
    getFieldValue: (path: string) => any,
): GridConfig[] =>
    grids.flatMap((grid) => {
        const hasMultiRowTemplates = !!(grid.expandableRowTemplates && grid.expandableRowTemplates.length > 0);
        // Nothing to expand if neither a per-column template nor a fieldPathTemplate nor multi-row templates are provided.
        if (
            !grid.fieldPathTemplate &&
            (!grid.rowFieldTemplates || grid.rowFieldTemplates.length === 0) &&
            !hasMultiRowTemplates
        )
            return [grid];

        // Expandable rows: split into parent header row + sub-rows per array item
        if (grid.expandableArrayField) {
            const arrayData = getFieldValue(grid.expandableArrayField);
            const items = Array.isArray(arrayData) ? arrayData : [];

            // Parent row: label + add button, with empty column cells. In single-row-per-item
            // mode, auto-wrap the parent + every sub-row in a single outer group cell so the
            // whole expandable block is bracketed by one row-group header. In multi-row mode we
            // don't wrap at the parent level — each item gets its own per-item group cell instead.
            const parentRow: GridConfig = {
                ...grid,
                rowSpan: undefined,
                columns: columnCount,
                fields: Array.from(
                    { length: columnCount },
                    () =>
                        ({
                            fieldPath: "",
                            label: "",
                            empty: true,
                        }) satisfies TextFieldConfig,
                ),
                ...(hasMultiRowTemplates
                    ? { rowGroupLabel: undefined, rowGroupSpan: undefined, skipRowGroupCell: true }
                    : {
                          rowGroupLabel: grid.rowGroupLabel ?? "",
                          rowGroupSpan: grid.rowGroupSpan ?? 1 + items.length,
                      }),
            };

            // Multi-row-per-item mode: each item expands into one sub-row per template.
            if (hasMultiRowTemplates) {
                const templates = grid.expandableRowTemplates!;
                const subRows: GridConfig[] = items.flatMap((_, rowIndex) =>
                    templates.map((tmpl, tmplIdx) => {
                        const tmplFields: FieldConfig[] = (tmpl.rowFieldTemplates || []).map((t) => {
                            const path = (t.fieldPath || "").replace("{rowIndex}", String(rowIndex));
                            const base = {
                                fieldPath: path,
                                label: t.label ?? "",
                                important: t.important ?? false,
                                empty: t.empty ?? false,
                                colSpan: t.colSpan,
                            };
                            switch (t.type) {
                                case "currency":
                                case "number":
                                    return { ...base, type: t.type } satisfies NumberFieldConfig;
                                case "checkbox":
                                    return { ...base, type: t.type } satisfies CheckboxFieldConfig;
                                case "date":
                                    return {
                                        ...base,
                                        type: t.type,
                                        checkDateFormat: t.checkDateFormat,
                                    } satisfies DateFieldConfig;
                                default:
                                    return { ...base, type: undefined } satisfies TextFieldConfig;
                            }
                        });
                        return {
                            ...grid,
                            rowLabel: tmpl.rowLabel ?? " ",
                            rowSpan: undefined,
                            expandableArrayField: undefined,
                            expandableParentField: grid.expandableArrayField,
                            expandableDescriptionTemplate: undefined,
                            expandableRowTemplates: undefined,
                            expandableSubRowIndex: rowIndex,
                            expandableItemRowIndex: tmplIdx,
                            fullRowSpan: tmpl.fullRowSpan ?? false,
                            mergedDataColumns: tmpl.mergedDataColumns ?? false,
                            rowLabelSpan: tmpl.rowLabelSpan,
                            rowFieldTemplates: undefined,
                            columns: columnCount,
                            fields: tmplFields,
                            // Auto-populate outer row-group cell info so nested row headers wrap
                            // all templates of the same item. Only the first row holds the cell.
                            rowGroupLabel: tmplIdx === 0 ? "" : undefined,
                            rowGroupSpan: tmplIdx === 0 ? templates.length : undefined,
                            skipRowGroupCell: tmplIdx !== 0,
                        } as GridConfig;
                    }),
                );
                return [parentRow, ...subRows];
            }

            // Each sub-row gets its own row label cell (description + delete) + currency fields
            const subRows: GridConfig[] = items.map((_, rowIndex) => ({
                ...grid,
                rowLabel: `__expandable_sub__${rowIndex}`,
                rowSpan: undefined,
                // Clear expandableArrayField on the sub-row so handleRenderRowLabel doesn't
                // treat it as a parent, but remember the parent field so the delete handler
                // mutates the correct array (multiple expandable grids can coexist in one section).
                expandableArrayField: undefined,
                expandableParentField: grid.expandableArrayField,
                expandableDescriptionTemplate: grid.expandableDescriptionTemplate
                    ? grid.expandableDescriptionTemplate.replace("{rowIndex}", String(rowIndex))
                    : undefined,
                expandableSubRowIndex: rowIndex,
                // Sub-rows always use the normal row-label + data-cells layout, even when the
                // parent row is rendered full-width.
                fullRowSpan: false,
                mergedDataColumns: false,
                // The parent row holds the outer group cell (rowSpan covers all sub-rows), so
                // each sub-row skips its own group cell.
                skipRowGroupCell: true,
                rowGroupLabel: undefined,
                rowGroupSpan: undefined,
                columns: columnCount,
                fields: buildTemplateFields(grid, columnCount, rowIndex),
            }));
            return [parentRow, ...subRows];
        }

        // Standard template expansion
        return [
            {
                ...grid,
                columns: columnCount,
                fields: buildTemplateFields(grid, columnCount),
            },
        ];
    });

const indexLabelSx = {
    fontWeight: 900,
    pr: 0.75,
    whiteSpace: "nowrap",
    fontSize: "0.95rem",
    alignSelf: "start",
} as const;

const FormRenderer = ({
    pageConfig,
    formType,
    formName,
    getFieldValue,
    handleFieldChange,
    getFieldConfidence,
    currentExtraction,
}: FormRendererProps) => {
    const formMeta = useMemo(
        () => ({ formType, formName, pageNumber: pageConfig.pageNumber }),
        [formType, formName, pageConfig.pageNumber],
    );
    const showOnlyImportant = useSelector<RootState, boolean>(({ ui }) => ui.showOnlyImportantFields);
    const theme = useTheme();
    const importantColor = theme.palette.mode === "dark" ? "#ffb74d" : "#f57c00";

    const renderField = (fieldConfig: FieldConfig) => {
        const {
            fieldPath = "",
            type = "text",
            width,
            helperText,
            important = false,
            labelStyle,
            inputStyle,
            colSpan,
            showWhen,
        } = fieldConfig;
        const rawLabelAny = (fieldConfig as { label?: unknown }).label;
        const rawLabel: string = typeof rawLabelAny === "string" ? rawLabelAny : "";
        const label = showOnlyImportant && fieldConfig.importantLabel ? fieldConfig.importantLabel : rawLabel;
        const value = getFieldValue(fieldPath);

        // Conditional visibility
        if (showWhen) {
            const watchedValue = getFieldValue(showWhen.field);
            const allowedValues = Array.isArray(showWhen.value) ? showWhen.value : [showWhen.value];
            const matches = Array.isArray(watchedValue)
                ? allowedValues.some((v) => watchedValue.includes(v))
                : allowedValues.includes(watchedValue);
            if (!matches) return null;
        }

        // Statement sections should always be visible, and arrays with important children should show.
        // Spacers are layout glue with no data and should always render to preserve grid alignment.
        if (
            showOnlyImportant &&
            !important &&
            type !== "statement-sections" &&
            type !== "spacer" &&
            !fieldHasImportantChildren(fieldConfig)
        )
            return null;

        let field: React.ReactNode = null;
        switch (type) {
            case "currency":
            case "number":
                field = (
                    <FormCurrencyField
                        key={fieldPath}
                        fieldPath={fieldPath}
                        label={label}
                        value={value}
                        handleFieldChange={handleFieldChange}
                        isImportant={important}
                        getFieldConfidence={getFieldConfidence}
                        currentExtraction={currentExtraction}
                        width={width}
                        helperText={helperText}
                        negativeOnly={fieldConfig.negativeOnly}
                        labelStyle={labelStyle}
                        inputStyle={inputStyle}
                        indexLabel={(fieldConfig as NumberFieldConfig).indexLabel}
                    />
                );
                break;

            case "select": {
                const selectConfig = fieldConfig as any;
                field = (
                    <FormSelect
                        key={fieldPath}
                        fieldPath={fieldPath}
                        label={label}
                        value={value}
                        options={selectConfig.options}
                        handleFieldChange={handleFieldChange}
                        isImportant={important}
                        width={width || "100%"}
                        labelStyle={labelStyle}
                        inputStyle={inputStyle}
                    />
                );
                break;
            }

            case "multi-select": {
                const multiConfig = fieldConfig as any;
                field = (
                    <FormSelect
                        key={fieldPath}
                        fieldPath={fieldPath}
                        label={label}
                        value={value}
                        options={multiConfig.options}
                        handleFieldChange={handleFieldChange}
                        isImportant={important}
                        width={width || "100%"}
                        labelStyle={labelStyle}
                        inputStyle={inputStyle}
                        multiple
                    />
                );
                break;
            }

            case "checkbox": {
                const checkboxConfig = fieldConfig as CheckboxFieldConfig;
                const checkboxControl = (
                    <FormControlLabel
                        key={fieldPath}
                        control={
                            <Checkbox
                                checked={value === true}
                                onChange={(e) => handleFieldChange(fieldPath, e.target.checked)}
                                size="small"
                            />
                        }
                        label={label}
                        labelPlacement={checkboxConfig.labelFirst ? "start" : "end"}
                        sx={{
                            ...(checkboxConfig.labelFirst && { ml: 0, mr: 0 }),
                            ...(checkboxConfig.labelGap != null && { gap: checkboxConfig.labelGap }),
                            "& .MuiFormControlLabel-label": {
                                ...labelStyle,
                                ...(important && { color: importantColor, fontWeight: 600 }),
                            },
                        }}
                    />
                );
                field = checkboxConfig.indexLabel ? (
                    <Box
                        key={fieldPath}
                        sx={{ display: "flex", alignItems: "center" }}
                    >
                        <Box
                            component="span"
                            sx={{ ...indexLabelSx, alignSelf: "center", ...(important && { color: importantColor }) }}
                        >
                            {checkboxConfig.indexLabel}
                        </Box>
                        {checkboxControl}
                    </Box>
                ) : (
                    checkboxControl
                );
                break;
            }

            case "dual-checkbox": {
                const dualConfig = fieldConfig as DualCheckboxFieldConfig;
                const [rawLabel0, rawLabel1] = dualConfig.label ?? [];
                const label0 = rawLabel0 || "Yes";
                const label1 = rawLabel1 || "No";
                const tupleValue = dualCheckboxValueToTuple(value);
                const handleToggle = (index: 0 | 1, checked: boolean) => {
                    const next: [boolean, boolean] = [tupleValue[0], tupleValue[1]];
                    next[index] = checked;
                    handleFieldChange(fieldPath, dualCheckboxTupleToValue(next));
                };
                const dualControl = (
                    <Box
                        key={fieldPath}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            pr: 2,
                            gap: dualConfig.labelGap ?? 1.5,
                            "& .MuiFormControlLabel-root": {
                                mr: 0,
                            },
                            "& .MuiCheckbox-root": {
                                pr: 0.1,
                            },
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={tupleValue[0]}
                                    onChange={(e) => handleToggle(0, e.target.checked)}
                                    size="small"
                                />
                            }
                            label={label0}
                            sx={{
                                "& .MuiFormControlLabel-label": {
                                    ...labelStyle,
                                    ...(important && { color: importantColor, fontWeight: 600 }),
                                },
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={tupleValue[1]}
                                    onChange={(e) => handleToggle(1, e.target.checked)}
                                    size="small"
                                />
                            }
                            label={label1}
                            sx={{
                                "& .MuiFormControlLabel-label": {
                                    ...labelStyle,
                                    ...(important && { color: importantColor, fontWeight: 600 }),
                                },
                            }}
                        />
                    </Box>
                );
                field = dualConfig.indexLabel ? (
                    <Box
                        key={fieldPath}
                        sx={{ display: "flex", alignItems: "center" }}
                    >
                        <Box
                            component="span"
                            sx={{ ...indexLabelSx, alignSelf: "center", ...(important && { color: importantColor }) }}
                        >
                            {dualConfig.indexLabel}
                        </Box>
                        {dualControl}
                    </Box>
                ) : (
                    dualControl
                );
                break;
            }

            case "radio": {
                const radioConfig = fieldConfig as any;
                field = (
                    <FormRadioGroup
                        key={fieldPath}
                        fieldPath={fieldPath}
                        label={label}
                        value={value}
                        options={radioConfig.options}
                        handleFieldChange={handleFieldChange}
                        labelStyle={labelStyle}
                    />
                );
                break;
            }

            case "array": {
                const arrayConfig = fieldConfig as any;
                const arrayIndexLabel = arrayConfig.indexLabel;
                field = (
                    <Box
                        key={fieldPath}
                        sx={{
                            gridColumn: "1 / -1",
                            ...(arrayIndexLabel && { display: "flex", alignItems: "flex-start", gap: 1 }),
                        }}
                    >
                        {arrayIndexLabel && (
                            <Box
                                component="span"
                                sx={{ ...indexLabelSx, mt: "6px", pr: 0, ...(important && { color: importantColor }) }}
                            >
                                {arrayIndexLabel}
                            </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <FormFieldArray
                                title={label}
                                items={Array.isArray(value) ? value : []}
                                fieldPath={fieldPath}
                                handleFieldChange={handleFieldChange}
                                emptyItem={arrayConfig.emptyItem}
                                addButtonLabel={arrayConfig.addButtonLabel}
                                emptyMessage={arrayConfig.emptyMessage}
                                columns={arrayConfig.itemColumns}
                                importantColor={important ? importantColor : undefined}
                                itemLabelTemplate={arrayConfig.itemLabelTemplate}
                                itemGap={arrayConfig.itemGap}
                                renderItem={(item, itemIndex, handleChange) => (
                                    <>
                                        {arrayConfig.itemFields.map((itemField: FieldConfig) => {
                                            // Skip non-important fields when showOnlyImportant is true
                                            const itemFieldIsImportant =
                                                itemField.important || fieldHasImportantChildren(itemField);
                                            if (showOnlyImportant && !itemFieldIsImportant) return null;

                                            const itemFieldPath = `${fieldPath}[${itemIndex}].${itemField.fieldPath ?? ""}`;
                                            const itemValue = get(item, itemField.fieldPath ?? "", "");
                                            const itemEffectiveColSpan =
                                                showOnlyImportant && itemField.importantColSpan
                                                    ? itemField.importantColSpan
                                                    : itemField.colSpan;

                                            if (itemField.type === "checkbox") {
                                                return wrapWithColSpan(
                                                    <FormControlLabel
                                                        key={itemFieldPath}
                                                        control={
                                                            <Checkbox
                                                                checked={itemValue || false}
                                                                onChange={(e) =>
                                                                    handleChange(
                                                                        itemIndex,
                                                                        itemField.fieldPath as any,
                                                                        e.target.checked,
                                                                    )
                                                                }
                                                                size="small"
                                                            />
                                                        }
                                                        label={itemField.label}
                                                    />,
                                                    itemFieldPath,
                                                    itemEffectiveColSpan,
                                                );
                                            }

                                            if (itemField.type === "currency" || itemField.type === "number") {
                                                return wrapWithColSpan(
                                                    <FormCurrencyField
                                                        key={itemFieldPath}
                                                        fieldPath={itemFieldPath}
                                                        label={itemField.label}
                                                        value={itemValue}
                                                        handleFieldChange={(_, v) =>
                                                            handleChange(itemIndex, itemField.fieldPath as any, v)
                                                        }
                                                        isImportant={itemField.important ?? false}
                                                        width={itemField.width}
                                                        labelStyle={itemField.labelStyle}
                                                        inputStyle={itemField.inputStyle}
                                                    />,
                                                    itemFieldPath,
                                                    itemEffectiveColSpan,
                                                );
                                            }

                                            // Handle nested arrays recursively
                                            if (itemField.type === "array") {
                                                const nestedArrayConfig = itemField as any;
                                                return (
                                                    <FormFieldArray
                                                        key={itemFieldPath}
                                                        title={itemField.label}
                                                        items={itemValue || []}
                                                        fieldPath={itemFieldPath}
                                                        handleFieldChange={handleFieldChange}
                                                        emptyItem={nestedArrayConfig.emptyItem}
                                                        addButtonLabel={nestedArrayConfig.addButtonLabel}
                                                        emptyMessage={nestedArrayConfig.emptyMessage}
                                                        renderItem={(nestedItem, nestedIndex, nestedHandleChange) => (
                                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                                {nestedArrayConfig.itemFields?.map(
                                                                    (nestedField: FieldConfig) => {
                                                                        // Skip non-important fields when showOnlyImportant is true
                                                                        const nestedFieldIsImportant =
                                                                            nestedField.important ||
                                                                            fieldHasImportantChildren(nestedField);
                                                                        if (
                                                                            showOnlyImportant &&
                                                                            !nestedFieldIsImportant
                                                                        )
                                                                            return null;

                                                                        const nestedFieldPath = `${itemFieldPath}[${nestedIndex}].${nestedField.fieldPath ?? ""}`;
                                                                        const nestedValue = get(
                                                                            nestedItem,
                                                                            nestedField.fieldPath ?? "",
                                                                            "",
                                                                        );

                                                                        if (nestedField.type === "checkbox") {
                                                                            return (
                                                                                <FormControlLabel
                                                                                    key={nestedFieldPath}
                                                                                    control={
                                                                                        <Checkbox
                                                                                            checked={
                                                                                                nestedValue || false
                                                                                            }
                                                                                            onChange={(e) =>
                                                                                                nestedHandleChange(
                                                                                                    nestedIndex,
                                                                                                    nestedField.fieldPath as any,
                                                                                                    e.target.checked,
                                                                                                )
                                                                                            }
                                                                                            size="small"
                                                                                        />
                                                                                    }
                                                                                    label={nestedField.label}
                                                                                />
                                                                            );
                                                                        }

                                                                        if (
                                                                            nestedField.type === "currency" ||
                                                                            nestedField.type === "number"
                                                                        ) {
                                                                            return (
                                                                                <FormCurrencyField
                                                                                    key={nestedFieldPath}
                                                                                    fieldPath={nestedFieldPath}
                                                                                    label={nestedField.label}
                                                                                    value={nestedValue}
                                                                                    handleFieldChange={(_, v) =>
                                                                                        nestedHandleChange(
                                                                                            nestedIndex,
                                                                                            nestedField.fieldPath as any,
                                                                                            v,
                                                                                        )
                                                                                    }
                                                                                    isImportant={
                                                                                        nestedField.important ?? false
                                                                                    }
                                                                                    width={nestedField.width}
                                                                                />
                                                                            );
                                                                        }

                                                                        return (
                                                                            <FormTextField
                                                                                key={nestedFieldPath}
                                                                                fieldPath={nestedFieldPath}
                                                                                label={nestedField.label}
                                                                                value={nestedValue}
                                                                                handleFieldChange={(_, v) =>
                                                                                    nestedHandleChange(
                                                                                        nestedIndex,
                                                                                        nestedField.fieldPath as any,
                                                                                        v,
                                                                                    )
                                                                                }
                                                                                isImportant={
                                                                                    nestedField.important ?? false
                                                                                }
                                                                                width={nestedField.width}
                                                                            />
                                                                        );
                                                                    },
                                                                )}
                                                            </Box>
                                                        )}
                                                    />
                                                );
                                            }

                                            return wrapWithColSpan(
                                                <FormTextField
                                                    key={itemFieldPath}
                                                    fieldPath={itemFieldPath}
                                                    label={itemField.label}
                                                    value={itemValue}
                                                    handleFieldChange={(_, v) =>
                                                        handleChange(itemIndex, itemField.fieldPath as any, v)
                                                    }
                                                    isImportant={itemField.important ?? false}
                                                    width={itemField.width}
                                                    labelStyle={itemField.labelStyle}
                                                    inputStyle={itemField.inputStyle}
                                                />,
                                                itemFieldPath,
                                                itemEffectiveColSpan,
                                            );
                                        })}
                                    </>
                                )}
                            />
                        </Box>
                    </Box>
                );
                break;
            }

            case "line-label": {
                const lineLabelConfig = fieldConfig as LineLabelFieldConfig;
                field = (
                    <Typography
                        key={fieldPath}
                        sx={{
                            fontWeight: 700,
                            fontSize: "0.8rem",
                            display: "flex",
                            alignItems: "center",
                            ...(important && { color: importantColor }),
                            ...labelStyle,
                        }}
                    >
                        {lineLabelConfig.indexLabel && (
                            <Box
                                component="span"
                                sx={{ ...indexLabelSx, ...(important && { color: importantColor }) }}
                            >
                                {lineLabelConfig.indexLabel}
                            </Box>
                        )}
                        {label}
                    </Typography>
                );
                break;
            }

            case "description-label": {
                const lineLabelCheckboxConfig = fieldConfig as LineLabelCheckboxFieldConfig;
                field = (
                    <Typography
                        key={fieldPath}
                        sx={{
                            fontWeight: 400,
                            fontSize: "0.85rem",
                            display: "flex",
                            alignItems: "center",
                            ...(important && { color: importantColor }),
                            ...labelStyle,
                        }}
                    >
                        {lineLabelCheckboxConfig.indexLabel && (
                            <Box
                                component="span"
                                sx={{ ...indexLabelSx, ...(important && { color: importantColor }) }}
                            >
                                {lineLabelCheckboxConfig.indexLabel}
                            </Box>
                        )}
                        {label}
                    </Typography>
                );
                break;
            }

            case "statement-sections":
                // Statement sections should always be visible
                field = (
                    <FormFieldStatementSections
                        key={fieldPath}
                        title={label}
                        statements={value || []}
                        fieldPath={fieldPath}
                        handleFieldChange={handleFieldChange}
                        getFieldConfidence={getFieldConfidence}
                    />
                );
                break;

            case "spacer":
                field = <Box />;
                break;

            case "date":
                field = (
                    <FormTextField
                        key={fieldPath}
                        fieldPath={fieldPath}
                        label={label}
                        value={value}
                        handleFieldChange={handleFieldChange}
                        isImportant={important}
                        getFieldConfidence={getFieldConfidence}
                        currentExtraction={currentExtraction}
                        width={width}
                        helperText={helperText}
                        labelStyle={labelStyle}
                        inputStyle={inputStyle}
                        checkDateFormat={(fieldConfig as DateFieldConfig).checkDateFormat}
                    />
                );
                break;

            case "text":
            default:
                field = (
                    <FormTextField
                        key={fieldPath}
                        fieldPath={fieldPath}
                        label={label}
                        value={value}
                        handleFieldChange={handleFieldChange}
                        isImportant={important}
                        getFieldConfidence={getFieldConfidence}
                        currentExtraction={currentExtraction}
                        width={width}
                        helperText={helperText}
                        labelStyle={labelStyle}
                        inputStyle={inputStyle}
                        indexLabel={(fieldConfig as TextFieldConfig).indexLabel}
                    />
                );
                break;
        }

        // Render subFields grouped under this field
        const { subFields, subFieldColumns, subFieldContainerStyle, childrenStyle, fieldStyle } = fieldConfig;
        if (subFields && subFields.length > 0) {
            const subFieldContent = subFieldColumns ? (
                <FormGrid
                    columns={subFieldColumns}
                    childrenStyle={childrenStyle}
                >
                    {subFields.map(renderField)}
                </FormGrid>
            ) : (
                subFields.map(renderField)
            );

            const hasIndexLabel = "indexLabel" in fieldConfig && fieldConfig.indexLabel;

            if (hasIndexLabel) {
                // Pull indexLabel out as a flex sibling so subFields align with the label text
                const isCheckbox = fieldConfig.type === "checkbox";
                const labelOnly = isCheckbox ? (
                    field
                ) : (
                    <Typography
                        key={fieldPath}
                        sx={{
                            fontWeight: 400,
                            fontSize: "0.85rem",
                            display: "flex",
                            alignItems: "center",
                            ...(important && { color: importantColor }),
                            ...labelStyle,
                        }}
                    >
                        {label}
                    </Typography>
                );

                field = (
                    <Box
                        key={fieldPath}
                        sx={{ display: "flex", alignItems: "flex-start", minWidth: 0 }}
                    >
                        {!isCheckbox && (
                            <Box
                                component="span"
                                sx={{ ...indexLabelSx, ...(important && { color: importantColor }) }}
                            >
                                {fieldConfig.indexLabel}
                            </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {labelOnly}
                            <Box
                                sx={{
                                    minWidth: 0,
                                    paddingTop: "0.5rem",
                                    paddingLeft: "1rem",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.75rem",
                                    ...subFieldContainerStyle,
                                }}
                            >
                                {subFieldContent}
                            </Box>
                        </Box>
                    </Box>
                );
            } else {
                field = (
                    <Box
                        key={fieldPath}
                        sx={{ minWidth: 0 }}
                    >
                        {field}
                        <Box
                            sx={{
                                minWidth: 0,
                                paddingTop: "0.5rem",
                                paddingLeft: "1.65rem",
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.75rem",
                                ...subFieldContainerStyle,
                            }}
                        >
                            {subFieldContent}
                        </Box>
                    </Box>
                );
            }
        }

        field = (
            <Box
                key={fieldPath}
                sx={{ minWidth: 0, ...fieldStyle }}
            >
                {field}
            </Box>
        );

        const effectiveColSpan =
            showOnlyImportant && fieldConfig.importantColSpan ? fieldConfig.importantColSpan : colSpan;

        const { fieldStyle: topLevelFieldStyle } = fieldConfig;

        if (effectiveColSpan && effectiveColSpan > 1) {
            return (
                <Box
                    key={fieldPath}
                    sx={{ gridColumn: `span ${effectiveColSpan}`, minWidth: 0, ...topLevelFieldStyle }}
                >
                    {field}
                </Box>
            );
        }

        if (topLevelFieldStyle) {
            return (
                <Box
                    key={fieldPath}
                    sx={{ minWidth: 0, ...topLevelFieldStyle }}
                >
                    {field}
                </Box>
            );
        }

        return field;
    };

    const renderGrid = (gridConfig: GridConfig, gridIndex: number) => (
        <FormGrid
            key={gridIndex}
            columns={gridConfig.columns || 2}
            gap={gridConfig.gap || 2}
            showDivider={gridConfig.showDivider}
            sx={gridConfig.sx}
            childrenStyle={gridConfig.childrenStyle}
        >
            {gridConfig.fields.map(renderField)}
        </FormGrid>
    );

    const renderSection = (sectionConfig: SectionConfig, sectionIndex: number) => {
        const grids = sectionConfig.grids || [];
        // For columnar-properties sections we defer row filtering to FormColumnarSection so the
        // show-important rules (important columns only → show all rows; important rows only →
        // show all columns; both → show everything) can be applied with full knowledge of the
        // table's row- and column-level importance.
        const isColumnarSection = sectionConfig.layout === "columnar-properties";
        const gridsToRender =
            showOnlyImportant && !isColumnarSection
                ? grids.filter((grid) => hasImportantFields(grid.fields) || grid.fieldImportantTemplate)
                : grids;

        if (gridsToRender.length === 0 && !sectionConfig.linkedArrayRows) return null;

        // Skip columnar sections entirely when showOnlyImportant and the table has no important
        // rows, columns, row-field templates, or section-level templates.
        if (showOnlyImportant && isColumnarSection) {
            const hasImportantSectionTemplates = (sectionConfig.rowFieldTemplates || []).some((t) => t.important);
            const hasImportantGridContent = grids.some(
                (g) =>
                    g.important ||
                    g.fieldImportantTemplate ||
                    g.fields.some((f) => f.important) ||
                    (g.rowFieldTemplates || []).some((t) => t.important),
            );
            if (!hasImportantSectionTemplates && !hasImportantGridContent) return null;
        }

        // Use columnar layout for sections with layout: 'columnar-properties'
        if (sectionConfig.layout === "columnar-properties") {
            const { columnLabels: staticLabels, linkedArrayField, columnLabelPrefix } = sectionConfig;

            // Dynamic columns driven by a linked array field
            if (linkedArrayField) {
                const arrayData = getFieldValue(linkedArrayField) || [];
                const actualCount = Array.isArray(arrayData) ? arrayData.length : 0;
                // When the backing array is empty, still render a single placeholder column.
                // Editing any cell promotes item[0] into existence via the normal write path.
                const count = Math.max(1, actualCount);
                const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
                const prefix = columnLabelPrefix || "";
                const dynamicLabels = Array.from({ length: count }, (_, i) => `${prefix}${letters[i] || i + 1}`);
                const expandedGrids = expandDynamicGrids(gridsToRender, count, getFieldValue);

                // renderRowLabel: parent row (label + add button) or sub-row (delete + description)
                const handleRenderRowLabel = (grid: GridConfig) => {
                    // Parent row: title + add button
                    if (grid.expandableArrayField) {
                        const arrField = grid.expandableArrayField;
                        const arrData = getFieldValue(arrField);
                        const items = Array.isArray(arrData) ? arrData : [];
                        return (
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, mb: 0.5 }}
                                >
                                    {grid.rowLabel}
                                </Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    size="small"
                                    variant="outlined"
                                    fullWidth={grid.fullRowSpan}
                                    onClick={() => {
                                        const newItem = JSON.parse(JSON.stringify(grid.expandableEmptyItem || {}));
                                        handleFieldChange(arrField, [...items, newItem]);
                                    }}
                                >
                                    {grid.expandableAddLabel || "Add"}
                                </Button>
                            </Box>
                        );
                    }
                    // Sub-row: delete button + description input
                    if (grid.expandableSubRowIndex !== undefined && grid.expandableDescriptionTemplate) {
                        const parentField =
                            grid.expandableParentField ??
                            gridsToRender.find((g) => g.expandableArrayField)?.expandableArrayField;
                        return (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                {parentField && (
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                            const arrData = getFieldValue(parentField);
                                            const items = Array.isArray(arrData) ? arrData : [];
                                            handleFieldChange(
                                                parentField,
                                                items.filter((_, j) => j !== grid.expandableSubRowIndex),
                                            );
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <Box sx={{ flex: 1 }}>
                                    {renderField({
                                        fieldPath: grid.expandableDescriptionTemplate,
                                        label: grid.expandableDescriptionLabel ?? "Description",
                                        type: "text",
                                        important: true,
                                    })}
                                </Box>
                            </Box>
                        );
                    }
                    // Multi-row-per-item sub-row: show the row's label. The delete button moves
                    // to the outer row-group cell (handleRenderRowGroupLabel) when a group column
                    // is configured; otherwise it renders here on the first template-row.
                    if (grid.expandableItemRowIndex !== undefined) {
                        if (sectionConfig.rowGroupHeader !== undefined) {
                            return <Box>{grid.rowLabel}</Box>;
                        }
                        const parentField =
                            grid.expandableParentField ??
                            gridsToRender.find((g) => g.expandableArrayField)?.expandableArrayField;
                        if (grid.expandableItemRowIndex === 0) {
                            return (
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                                    {parentField && (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                                const arrData = getFieldValue(parentField);
                                                const items = Array.isArray(arrData) ? arrData : [];
                                                handleFieldChange(
                                                    parentField,
                                                    items.filter((_, j) => j !== grid.expandableSubRowIndex),
                                                );
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    <Box sx={{ flex: 1, fontWeight: 600 }}>{grid.rowLabel}</Box>
                                </Box>
                            );
                        }
                        return <Box sx={{ pl: 4 }}>{grid.rowLabel}</Box>;
                    }
                    return undefined;
                };

                const handleRenderRowGroupLabel = (grid: GridConfig) => {
                    if (grid.expandableItemRowIndex === 0 && grid.rowGroupLabel !== undefined) {
                        const parentField =
                            grid.expandableParentField ??
                            gridsToRender.find((g) => g.expandableArrayField)?.expandableArrayField;
                        return (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
                                <Box sx={{ fontWeight: 700 }}>{grid.rowGroupLabel}</Box>
                                {parentField && (
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                            const arrData = getFieldValue(parentField);
                                            const items = Array.isArray(arrData) ? arrData : [];
                                            handleFieldChange(
                                                parentField,
                                                items.filter((_, j) => j !== grid.expandableSubRowIndex),
                                            );
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        );
                    }
                    return undefined;
                };

                return (
                    <FormColumnarSection
                        key={sectionIndex}
                        title={sectionConfig.title}
                        showDivider={sectionConfig.showDivider}
                        columnLabels={dynamicLabels}
                        columnGroupLabels={sectionConfig.columnGroupLabels}
                        rowLabelHeader={sectionConfig.rowLabelHeader}
                        rowLabelColumnWidth={sectionConfig.rowLabelColumnWidth}
                        rowGroupHeader={sectionConfig.rowGroupHeader}
                        rowGroupColumnWidth={sectionConfig.rowGroupColumnWidth}
                        columnWidths={sectionConfig.columnWidths}
                        columnGroupWidths={sectionConfig.columnGroupWidths}
                        maxFixedColumns={sectionConfig.maxFixedColumns}
                        grids={expandedGrids}
                        renderField={renderField}
                        showOnlyImportant={showOnlyImportant}
                        renderRowLabel={(grid) => handleRenderRowLabel(grid)}
                        renderRowGroupLabel={(grid) => handleRenderRowGroupLabel(grid)}
                    />
                );
            }

            // Dynamic rows driven by an array field with fixed columns
            if (staticLabels && sectionConfig.linkedArrayRows) {
                const arrayData = getFieldValue(sectionConfig.linkedArrayRows) || [];
                const items = Array.isArray(arrayData) ? arrayData : [];
                const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
                const templates = sectionConfig.rowFieldTemplates || [];
                const columnCount = staticLabels.length;
                // When the backing array is empty, render a single placeholder row.
                // Editing any cell promotes item[0] into existence via the normal write path.
                const rowCount = Math.max(1, items.length);

                // Generate one grid (row) per (real or placeholder) array item
                const dynamicGrids: GridConfig[] = Array.from({ length: rowCount }, (_, rowIdx) => ({
                    rowLabel: letters[rowIdx] || String(rowIdx + 1),
                    columns: columnCount,
                    fields: templates.map(
                        (tpl) =>
                            ({
                                fieldPath: tpl.fieldPath.replace("{index}", String(rowIdx)),
                                label: "",
                                type: tpl.type || "currency",
                                important: tpl.important,
                            }) as FieldConfig,
                    ),
                }));

                // Append static grids (totals rows from grids), filtering by importance when needed
                const staticGrids = showOnlyImportant ? grids.filter((g) => g.fields.some((f) => f.important)) : grids;
                const allGrids = [...dynamicGrids, ...staticGrids];

                return (
                    <FormColumnarSection
                        key={sectionIndex}
                        title={sectionConfig.title}
                        showDivider={sectionConfig.showDivider}
                        columnLabels={staticLabels}
                        columnGroupLabels={sectionConfig.columnGroupLabels}
                        rowLabelHeader={sectionConfig.rowLabelHeader}
                        rowLabelColumnWidth={sectionConfig.rowLabelColumnWidth}
                        rowGroupHeader={sectionConfig.rowGroupHeader}
                        rowGroupColumnWidth={sectionConfig.rowGroupColumnWidth}
                        columnWidths={sectionConfig.columnWidths}
                        columnGroupWidths={sectionConfig.columnGroupWidths}
                        maxFixedColumns={sectionConfig.maxFixedColumns}
                        grids={allGrids}
                        renderField={renderField}
                        showOnlyImportant={showOnlyImportant}
                    />
                );
            }

            if (staticLabels) {
                // Expand any grids with expandableArrayField into parent + sub-rows, then
                // reuse the shared row-label renderer so parent rows show the Add button and
                // sub-rows show the delete + description input — even when columns are static.
                const columnCount = staticLabels.length;
                const expandedGrids = expandDynamicGrids(gridsToRender, columnCount, getFieldValue);

                const handleRenderRowLabel = (grid: GridConfig) => {
                    if (grid.expandableArrayField) {
                        const arrField = grid.expandableArrayField;
                        const arrData = getFieldValue(arrField);
                        const items = Array.isArray(arrData) ? arrData : [];
                        return (
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, mb: 0.5 }}
                                >
                                    {grid.rowLabel}
                                </Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    size="small"
                                    variant="outlined"
                                    fullWidth={grid.fullRowSpan}
                                    onClick={() => {
                                        const newItem = JSON.parse(JSON.stringify(grid.expandableEmptyItem || {}));
                                        handleFieldChange(arrField, [...items, newItem]);
                                    }}
                                >
                                    {grid.expandableAddLabel || "Add"}
                                </Button>
                            </Box>
                        );
                    }
                    if (grid.expandableSubRowIndex !== undefined && grid.expandableDescriptionTemplate) {
                        const parentField =
                            grid.expandableParentField ??
                            gridsToRender.find((g) => g.expandableArrayField)?.expandableArrayField;
                        return (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                {parentField && (
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                            const arrData = getFieldValue(parentField);
                                            const items = Array.isArray(arrData) ? arrData : [];
                                            handleFieldChange(
                                                parentField,
                                                items.filter((_, j) => j !== grid.expandableSubRowIndex),
                                            );
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <Box sx={{ flex: 1 }}>
                                    {renderField({
                                        fieldPath: grid.expandableDescriptionTemplate,
                                        label: grid.expandableDescriptionLabel ?? "Description",
                                        type: "text",
                                        important: true,
                                    })}
                                </Box>
                            </Box>
                        );
                    }
                    // Multi-row-per-item sub-row: show the row's label. The delete button moves
                    // to the outer row-group cell (handleRenderRowGroupLabel) when a group column
                    // is configured; otherwise it renders here on the first template-row.
                    if (grid.expandableItemRowIndex !== undefined) {
                        if (sectionConfig.rowGroupHeader !== undefined) {
                            return <Box>{grid.rowLabel}</Box>;
                        }
                        const parentField =
                            grid.expandableParentField ??
                            gridsToRender.find((g) => g.expandableArrayField)?.expandableArrayField;
                        if (grid.expandableItemRowIndex === 0) {
                            return (
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                                    {parentField && (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                                const arrData = getFieldValue(parentField);
                                                const items = Array.isArray(arrData) ? arrData : [];
                                                handleFieldChange(
                                                    parentField,
                                                    items.filter((_, j) => j !== grid.expandableSubRowIndex),
                                                );
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    <Box sx={{ flex: 1, fontWeight: 600 }}>{grid.rowLabel}</Box>
                                </Box>
                            );
                        }
                        return <Box sx={{ pl: 4 }}>{grid.rowLabel}</Box>;
                    }
                    return undefined;
                };

                const handleRenderRowGroupLabel = (grid: GridConfig) => {
                    if (grid.expandableItemRowIndex === 0 && grid.rowGroupLabel !== undefined) {
                        const parentField =
                            grid.expandableParentField ??
                            gridsToRender.find((g) => g.expandableArrayField)?.expandableArrayField;
                        return (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
                                <Box sx={{ fontWeight: 700 }}>{grid.rowGroupLabel}</Box>
                                {parentField && (
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                            const arrData = getFieldValue(parentField);
                                            const items = Array.isArray(arrData) ? arrData : [];
                                            handleFieldChange(
                                                parentField,
                                                items.filter((_, j) => j !== grid.expandableSubRowIndex),
                                            );
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        );
                    }
                    return undefined;
                };

                return (
                    <FormColumnarSection
                        key={sectionIndex}
                        title={sectionConfig.title}
                        showDivider={sectionConfig.showDivider}
                        columnLabels={staticLabels}
                        columnGroupLabels={sectionConfig.columnGroupLabels}
                        rowLabelHeader={sectionConfig.rowLabelHeader}
                        rowLabelColumnWidth={sectionConfig.rowLabelColumnWidth}
                        rowGroupHeader={sectionConfig.rowGroupHeader}
                        rowGroupColumnWidth={sectionConfig.rowGroupColumnWidth}
                        columnWidths={sectionConfig.columnWidths}
                        columnGroupWidths={sectionConfig.columnGroupWidths}
                        maxFixedColumns={sectionConfig.maxFixedColumns}
                        grids={expandedGrids}
                        renderField={renderField}
                        showOnlyImportant={showOnlyImportant}
                        renderRowLabel={(grid) => handleRenderRowLabel(grid)}
                        renderRowGroupLabel={(grid) => handleRenderRowGroupLabel(grid)}
                    />
                );
            }
        }

        return (
            <FormSection
                key={sectionIndex}
                title={sectionConfig.title}
                showDivider={sectionConfig.showDivider}
            >
                {gridsToRender.map(renderGrid)}
            </FormSection>
        );
    };

    return (
        <FormMetaContext.Provider value={formMeta}>
            <Box sx={{ pb: 4 }}>{pageConfig.sections.map(renderSection)}</Box>
        </FormMetaContext.Provider>
    );
};

export default FormRenderer;
